import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';

export interface DashboardFilters {
  [key: string]: any;
}

interface DashboardFilterContextType {
  filters: DashboardFilters;
  setFilters: (filters: DashboardFilters) => void;
  updateFilter: (key: string, value: any) => void;
  clearFilters: () => void;
}

const DashboardFilterContext = createContext<
  DashboardFilterContextType | undefined
>(undefined);

export const useDashboardFilters = () => {
  const context = useContext(DashboardFilterContext);
  if (!context) {
    return {
      filters: {},
      setFilters: () => {},
      updateFilter: () => {},
      clearFilters: () => {},
    };
  }
  return context;
};

const FILTER_PARAM = 'filters';

function serializeFilters(filters: DashboardFilters): string {
  const nonEmpty = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => {
      if (v == null || v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    }),
  );
  if (Object.keys(nonEmpty).length === 0) return '';
  return btoa(JSON.stringify(nonEmpty));
}

function deserializeFilters(encoded: string): DashboardFilters {
  if (!encoded) return {};
  try {
    return JSON.parse(atob(encoded));
  } catch {
    return {};
  }
}

interface DashboardFilterProviderProps {
  children: ReactNode;
}

export const DashboardFilterProvider: React.FC<
  DashboardFilterProviderProps
> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL params
  const [filters, setFiltersState] = useState<DashboardFilters>(() =>
    deserializeFilters(searchParams.get(FILTER_PARAM) || ''),
  );

  // Sync filters to URL when they change
  useEffect(() => {
    const encoded = serializeFilters(filters);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (encoded) {
          next.set(FILTER_PARAM, encoded);
        } else {
          next.delete(FILTER_PARAM);
        }
        return next;
      },
      { replace: true },
    );
  }, [filters, setSearchParams]);

  const setFilters = useCallback((newFilters: DashboardFilters) => {
    setFiltersState(newFilters);
  }, []);

  const updateFilter = useCallback((key: string, value: any) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  return (
    <DashboardFilterContext.Provider
      value={{ filters, setFilters, updateFilter, clearFilters }}
    >
      {children}
    </DashboardFilterContext.Provider>
  );
};
